import {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  HandleStyles,
  Rnd,
  RndDragEvent,
  RndResizeCallback,
  RndResizeStartCallback,
} from 'react-rnd'

import { useBringToFront } from '@/hooks/useBringToFront'
import './floatingVideoWindow.css'
import { useMissionVideoWidget } from './useMissionVideoWidget'
import {
  ASPECT_RATIO_VIDEO,
  DEFAULT_WINDOW_WIDTH,
  MIN_WINDOW_WIDTH,
  ReportedSize,
  WIDGET_CHROME_WIDTH,
  clampPosition,
  estimateFrameHeight,
  getAspectLockExtraHeight,
  getInitialPosition,
  getMinWindowHeight,
} from './windowGeometry'
import { WindowVideoMinimized } from './windowMinimized'

/**
 * ドラッグで窓を動かす帯のクラス名（Rnd の dragHandleClassName）。帯は Widget の
 * ヘッダーの上に重ねる透明な要素で、寸法は floatingVideoWindow.css に書く
 */
const DRAG_HANDLE_CLASS = 'window-video-drag-handle'
/** 初期位置を置く前の、画面外の位置 */
const OFFSCREEN_POSITION = { x: 3000, y: 3000 }
/** 幅変更つまみが窓の右端からはみ出す量。ホバーで少し引き出す */
const RESIZE_HANDLER_OFFSET = -20
const RESIZE_HANDLER_OFFSET_HOVER = -35

/**
 * 辺・角のつかみ代（Rnd の resizeHandleStyles）。ドラッグ帯（z-index: 1）より
 * 前に置き、帯と重なる窓の上端・左上でもつかめるようにする
 */
const RESIZE_HANDLE_STYLE = { zIndex: 2 }
const RESIZE_HANDLE_STYLES: HandleStyles = {
  top: RESIZE_HANDLE_STYLE,
  right: RESIZE_HANDLE_STYLE,
  bottom: RESIZE_HANDLE_STYLE,
  left: RESIZE_HANDLE_STYLE,
  topRight: RESIZE_HANDLE_STYLE,
  bottomRight: RESIZE_HANDLE_STYLE,
  bottomLeft: RESIZE_HANDLE_STYLE,
  topLeft: RESIZE_HANDLE_STYLE,
}

/** マウント直後（Widget の通知が届く前）の窓の大きさ */
const INITIAL_SIZE = {
  width: DEFAULT_WINDOW_WIDTH,
  height: estimateFrameHeight(DEFAULT_WINDOW_WIDTH, null),
}

const getViewport = (): { width: number; height: number } => ({
  width: window.innerWidth,
  height: window.innerHeight,
})

/** 通知が今の値と同じなら、今の値をそのまま返す（再描画させない） */
const mergeReport = (
  prev: ReportedSize | null,
  next: ReportedSize,
): ReportedSize =>
  prev?.width === next.width && prev.height === next.height ? prev : next

/**
 * touchcancel で取り消されたタッチを changedTouches に入れた touchend を作る。
 * react-draggable は終わりのイベントの型を見ず、changedTouches に自分のタッチ
 * （identifier）があるかどうかだけで終了処理を進めるので、TouchEvent でなくても
 * 同じ値を持つ Event で終わらせられる。辺・角のリサイズ（re-resizable）も、
 * touchend の種類だけ見て終わるので同じ
 */
const createTouchEnd = (cancelled: TouchEvent): Event => {
  const changedTouches = Array.from(cancelled.changedTouches)
  const touches = Array.from(cancelled.touches)
  try {
    return new TouchEvent('touchend', {
      bubbles: true,
      cancelable: true,
      changedTouches,
      touches,
      targetTouches: [],
    })
  } catch {
    // TouchEvent を作れない環境（古い iPadOS など）のための分岐。通常の Event に
    // 同じ値を持たせる
    const touchEnd = new Event('touchend', { bubbles: true, cancelable: true })
    Object.defineProperty(touchEnd, 'changedTouches', { value: changedTouches })
    Object.defineProperty(touchEnd, 'touches', { value: touches })
    Object.defineProperty(touchEnd, 'targetTouches', { value: [] })
    return touchEnd
  }
}

/**
 * identifier を読むための最小の形。DOM の TouchList、React の TouchList、配列の
 * どれも満たす
 */
type TouchesLike = ArrayLike<{ readonly identifier: number }>

/** 先頭の指の identifier。指が 1 本も無ければ null */
const getFirstIdentifier = (touches: TouchesLike): number | null =>
  touches.length > 0 ? touches[0].identifier : null

/**
 * ドラッグを始めた指の identifier。react-draggable がドラッグ中の指として追う指
 * （DraggableCore の getTouchIdentifier）と同じ規則で選ぶ: targetTouches の先頭、
 * 無ければ changedTouches の先頭。マウスのドラッグには指が無いので null。
 * タッチの開始は react-draggable がネイティブの touchstart リスナーで受けるため、
 * タッチなら（ネイティブの）TouchEvent、マウスなら（React の）MouseEvent が来る。
 * identifier は 0 も有効な値なので、指の有無は null かどうかで判断する
 */
const getDragTouchIdentifier = (e: RndDragEvent): number | null => {
  if (!('targetTouches' in e)) return null
  return (
    getFirstIdentifier(e.targetTouches) ?? getFirstIdentifier(e.changedTouches)
  )
}

/** touches に、この identifier の指が入っているか */
const hasTouch = (touches: TouchesLike, identifier: number): boolean =>
  Array.from(touches).some((touch) => touch.identifier === identifier)

interface FloatingVideoWindowProps {
  /** true になったら、チュートリアル終了として自動再生で Widget を作り直す */
  readonly startVideo: boolean
}

/**
 * ミッションの動画窓。digitane 動画 Widget（floating モード）を入れる枠だけを持つ
 * 薄いシェルで、位置（ドラッグ）・大きさ（辺・角、右側のつまみ）・最小化と復元・
 * 前面化を担当する。
 * ヘッダー・動画・終了ボタン・チャプター一覧・EXP 演出は Widget（iframe）の中にあり、
 * 窓の高さは Widget が通知する window:resized に従う。
 *
 * 辺・角のリサイズはマイクラッチの動画窓と同じ仕様で、Rnd の 8 方向のつかみに
 * 動画部分の 16:9 を保たせる（lockAspectRatio とその余白）。
 *
 * iframe の上では親のマウスイベントが取れないため、ドラッグは Widget のヘッダーの上に
 * 重ねた透明な帯（親の DOM）で行い、リサイズ中とドラッグ中は iframe を
 * pointer-events: none にする。
 */
export const FloatingVideoWindow = ({
  startVideo,
}: FloatingVideoWindowProps) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)
  const { order, bringToFront } = useBringToFront()

  const [position, setPosition] = useState(OFFSCREEN_POSITION)
  const [width, setWidth] = useState(DEFAULT_WINDOW_WIDTH)
  // Widget が最後に通知した高さと、そのときの幅
  const [lastReport, setLastReport] = useState<ReportedSize | null>(null)
  const [visible, setVisible] = useState(true)
  // リサイズ中・ドラッグ中は iframe がマウスイベントを奪わないようにする。
  // リサイズは、右側のつまみ（自前）と、辺・角（Rnd）の 2 系統
  const [isHandleResizing, setIsHandleResizing] = useState(false)
  const [isEdgeResizing, setIsEdgeResizing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [resizeHandlerPosition, setResizeHandlerPosition] = useState(
    RESIZE_HANDLER_OFFSET,
  )

  // 窓の高さは iframe ホストの高さと同じ（ドラッグ帯は iframe の上に重ねるため高さを足さない）
  const windowHeight = estimateFrameHeight(width, lastReport)

  const widthRef = useRef(width)
  widthRef.current = width

  const isResizing = isHandleResizing || isEdgeResizing
  const isResizingRef = useRef(isResizing)
  isResizingRef.current = isResizing
  // リサイズの操作中に届いた最後の通知。操作が終わるまで窓の寸法に反映しない
  const pendingReportRef = useRef<ReportedSize | null>(null)

  const { status, retry } = useMissionVideoWidget({
    targetRef,
    startVideo,
    onResized: (height) => {
      const report = { width: widthRef.current, height }
      // リサイズの操作中は、通知で窓の寸法を動かさない。通知の高さは Widget が受け
      // 取った幅での値で、操作中の幅とずれることがある。反映すると、辺・角のリサイズが
      // 16:9 の計算に使う動画以外の高さ（extraHeight）が操作中に揺れ、窓の高さが
      // 跳ねる。最後の通知は、操作が終わってから合わせる（下の effect）
      if (isResizingRef.current) {
        pendingReportRef.current = report
        return
      }
      setLastReport((prev) => mergeReport(prev, report))
    },
    onMinimized: () => setVisible(false),
  })

  // リサイズの操作が終わったら、操作中に届いた最後の通知に合わせる
  // （幅は操作で決まったまま、高さが通知に追従する）
  useEffect(() => {
    if (isResizing) return

    const pending = pendingReportRef.current
    if (pending === null) return

    pendingReportRef.current = null
    setLastReport((prev) => mergeReport(prev, pending))
  }, [isResizing])

  // 初期表示は「接続のやり方」タブ（右端固定・幅 140）の真横に置く。
  // 描画前に置いて、画面外の位置が見えないようにする
  useLayoutEffect(() => {
    setPosition(getInitialPosition(INITIAL_SIZE, getViewport()))
  }, [])

  // 窓の大きさか画面の大きさが変わったら、窓が画面内に収まる位置へ寄せる。
  // リスナーは同じ関数参照で add / remove する
  const clampToViewport = useCallback(() => {
    setPosition((prev) => {
      const next = clampPosition(
        prev,
        { width, height: windowHeight },
        getViewport(),
      )
      return next.x === prev.x && next.y === prev.y ? prev : next
    })
  }, [width, windowHeight])

  useEffect(() => {
    clampToViewport()
    window.addEventListener('resize', clampToViewport)
    return () => {
      window.removeEventListener('resize', clampToViewport)
    }
  }, [clampToViewport])

  // 初期表示ではプレビューより動画を前面にする（学習動画を見ながら組む導線を
  // 優先）。マウント時に一度だけ自分を最前面へ登録する。以降の前後関係は
  // 「最後に触った窓が前」。
  useEffect(() => {
    bringToFront()
    // bringToFront は useCallback（id 固定）で安定しているため初回のみ実行される。
  }, [bringToFront])

  // Widget は iframe のため、動画面のクリックは親 DOM へ届かず Rnd の
  // onMouseDown では前面化できない。iframe クリックでウィンドウのフォーカスが
  // iframe へ移る（window blur）ことを利用して前面化する。
  useEffect(() => {
    const handleWindowBlur = (): void => {
      const active = document.activeElement
      if (
        active instanceof HTMLIFrameElement &&
        layerRef.current?.contains(active)
      ) {
        bringToFront()
      }
    }
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [bringToFront])

  // 右側のつまみによる幅の変更（辺・角のリサイズは Rnd が行う）。
  // iframe の上ではマウスイベントが取れないため、
  // document の capture リスナーで追う（開始時に iframe は pointer-events: none になる）
  const resizing = useRef(false)
  const initialX = useRef(0)
  const initialWidth = useRef(0)
  // マウスボタンの状態を追跡（ボタンを放した mouseup を取りこぼしたときの救済）
  const mouseButtonPressed = useRef(false)
  const safetyTimerRef = useRef<number | null>(null)

  const handleResizing = useCallback((e: MouseEvent | TouchEvent) => {
    if (!resizing.current) return

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    // マウス移動量に基づいて新しい幅を計算（Widget が fullsize に降格する幅未満にしない）
    const deltaX = clientX - initialX.current
    setWidth(Math.max(MIN_WINDOW_WIDTH, initialWidth.current + deltaX))
  }, [])

  const removeResizeListeners = useCallback(
    (onEnd: () => void) => {
      document.removeEventListener('mousemove', handleResizing, {
        capture: true,
      })
      document.removeEventListener('mouseup', onEnd, { capture: true })
      document.removeEventListener('touchmove', handleResizing, {
        capture: true,
      })
      document.removeEventListener('touchend', onEnd, { capture: true })
      document.removeEventListener('touchcancel', onEnd, { capture: true })
    },
    [handleResizing],
  )

  const handleEndResizing = useCallback(() => {
    if (!resizing.current) return

    resizing.current = false
    setIsHandleResizing(false)
    mouseButtonPressed.current = false

    // セーフティタイマーをクリア
    if (safetyTimerRef.current !== null) {
      clearTimeout(safetyTimerRef.current)
      safetyTimerRef.current = null
    }

    removeResizeListeners(handleEndResizing)
  }, [removeResizeListeners])

  // グローバルマウスボタン監視。ボタンが放されたのに終了イベントが検知されなかった
  // 場合に、リサイズを終わらせる
  const checkMouseButtonState = useCallback(
    (e: MouseEvent) => {
      if (e.buttons === 0 && mouseButtonPressed.current) {
        mouseButtonPressed.current = false
        if (resizing.current) {
          handleEndResizing()
        }
      } else if (e.buttons > 0) {
        mouseButtonPressed.current = true
      }
    },
    [handleEndResizing],
  )

  useEffect(() => {
    document.addEventListener('mousemove', checkMouseButtonState)
    return () => {
      document.removeEventListener('mousemove', checkMouseButtonState)
    }
  }, [checkMouseButtonState])

  // アンマウント時にタイマーとリスナーを片付ける
  useEffect(() => {
    return () => {
      if (safetyTimerRef.current !== null) {
        clearTimeout(safetyTimerRef.current)
        safetyTimerRef.current = null
      }
      removeResizeListeners(handleEndResizing)
    }
  }, [handleEndResizing, removeResizeListeners])

  const handleStartResizing = (e: ReactMouseEvent | ReactTouchEvent) => {
    e.stopPropagation()
    e.preventDefault() // マウスのドラッグイベントをキャンセル
    bringToFront() // mousedown は stopPropagation で Rnd へ届かないため明示的に前面化

    // 初期マウス位置と初期幅を記録
    initialX.current = 'touches' in e ? e.touches[0].clientX : e.clientX
    initialWidth.current = widthRef.current

    resizing.current = true
    setIsHandleResizing(true)
    mouseButtonPressed.current = true

    document.addEventListener('mousemove', handleResizing, { capture: true })
    document.addEventListener('mouseup', handleEndResizing, { capture: true })
    document.addEventListener('touchmove', handleResizing, { capture: true })
    document.addEventListener('touchend', handleEndResizing, { capture: true })
    // タッチが取り消されると touchend ではなく touchcancel が届く。拾わないと
    // isHandleResizing が true のまま残り、画面全体のレイヤーがポインターを受け続ける
    document.addEventListener('touchcancel', handleEndResizing, {
      capture: true,
    })

    // セーフティタイマー: マウスボタンを放しても終了イベントが検知されなかった場合のバックアップ
    safetyTimerRef.current = window.setTimeout(() => {
      if (!mouseButtonPressed.current && resizing.current) {
        handleEndResizing()
      }
    }, 300)
  }

  const handleResizeHandlerMouseEnter = () => {
    if (!resizing.current) {
      setResizeHandlerPosition(RESIZE_HANDLER_OFFSET_HOVER)
    }
  }

  const handleResizeHandlerMouseLeave = () => {
    if (!resizing.current) {
      setResizeHandlerPosition(RESIZE_HANDLER_OFFSET)
    }
  }

  // ドラッグを始めた指の identifier（react-draggable がドラッグ中の指として追う指）。
  // マウスのドラッグでは null。ドラッグが終わると handleDragStop で null に戻す
  const dragTouchIdentifier = useRef<number | null>(null)

  const handleDragStart = (e: RndDragEvent) => {
    dragTouchIdentifier.current = getDragTouchIdentifier(e)
    setIsDragging(true)
  }

  const handleDragStop = (_e: unknown, d: { x: number; y: number }) => {
    dragTouchIdentifier.current = null
    setPosition(
      clampPosition(d, { width, height: windowHeight }, getViewport()),
    )
    setIsDragging(false)
  }

  // react-draggable 4.4.6 は touchend しかドラッグの終わりと見なさない。touchcancel では
  // 内部のドラッグ（document の touchmove / touchend のリスナーと選択禁止のスタイル）が
  // 残り、identifier を使い回すブラウザでは次のタッチで古いドラッグが続く。ドラッグ中だけ
  // touchcancel を拾い、ドラッグを始めた指（dragTouchIdentifier）が取り消されたときに、
  // その指を changedTouches に入れた touchend（createTouchEnd）を document へ送って
  // react-draggable 自身に終わらせる（onStop 経由で handleDragStop が位置を寄せ、
  // isDragging を戻す）。react-draggable は自分の指が changedTouches に無い touchend では
  // 何も終わらせないので、ドラッグに使っていない指（palm rejection で取り消された指など）や、
  // 追う指が無いマウスのドラッグでの touchcancel は何もしない。ここで isDragging を戻すと
  // touchcancel のリスナーまで外れ、その後にドラッグ中の指が取り消されても終わらせられず、
  // 古いドラッグが残ってしまう。Event も作れない極端な環境や、送っても isDragging が残る
  // 場合に備え、最後に isDragging も戻す（画面全体のレイヤーがポインターを受け続けない
  // ように）
  useEffect(() => {
    if (!isDragging) return

    const handleTouchCancel = (e: TouchEvent): void => {
      const identifier = dragTouchIdentifier.current
      // ドラッグに使っていない指（マウスのドラッグでは、どの指も）の取り消し。
      // ドラッグは続いているので、isDragging もこのリスナーもそのままにする
      if (identifier === null || !hasTouch(e.changedTouches, identifier)) return

      try {
        document.dispatchEvent(createTouchEnd(e))
      } catch {
        // Event も作れない極端な環境。下の setIsDragging だけで戻す
      }
      setIsDragging(false)
    }
    document.addEventListener('touchcancel', handleTouchCancel, {
      capture: true,
    })
    return () => {
      document.removeEventListener('touchcancel', handleTouchCancel, {
        capture: true,
      })
    }
  }, [isDragging])

  // 辺・角のリサイズ。幅と位置は Rnd が求めた値（lockAspectRatio で 16:9 を保つ）に
  // 従い、高さは幅と直近の通知から決まる（windowHeight）
  const handleEdgeResizeStart: RndResizeStartCallback = () => {
    // Rnd は mousedown をここで止めるため、Rnd の onMouseDown では前面化できない
    bringToFront()
    setIsEdgeResizing(true)
  }

  const handleEdgeResize: RndResizeCallback = (_e, _dir, ref, _delta, pos) => {
    // Rnd が窓の要素に付けた幅（px）がそのまま新しい幅になる
    const resizedWidth = Number.parseFloat(ref.style.width)
    if (Number.isFinite(resizedWidth)) setWidth(resizedWidth)
    setPosition(pos)
  }

  const handleEdgeResizeStop: RndResizeCallback = (
    _e,
    _dir,
    _ref,
    _delta,
    pos,
  ) => {
    // 右側のつまみが画面内に残る位置へ寄せる（ドラッグの終わりと同じ）
    setPosition(
      clampPosition(pos, { width, height: windowHeight }, getViewport()),
    )
    setIsEdgeResizing(false)
  }

  // re-resizable は touchend しか辺・角のリサイズの終わりと見なさない。タッチが取り消され
  // る（touchcancel）と終わりが来ず、isEdgeResizing が true のまま残り、画面全体のレイヤー
  // がポインターを受け続ける。リサイズ中だけ touchcancel を拾い、同じ指の touchend
  // （createTouchEnd）を送って re-resizable 自身に終わらせる（onResizeStop 経由で
  // handleEdgeResizeStop が位置を寄せ、isEdgeResizing を戻す）。送れない環境のために、
  // 最後に isEdgeResizing も戻す
  useEffect(() => {
    if (!isEdgeResizing) return

    const handleTouchCancel = (e: TouchEvent): void => {
      try {
        document.dispatchEvent(createTouchEnd(e))
      } catch {
        // Event も作れない極端な環境。下の setIsEdgeResizing だけで戻す
      }
      setIsEdgeResizing(false)
    }
    document.addEventListener('touchcancel', handleTouchCancel, {
      capture: true,
    })
    return () => {
      document.removeEventListener('touchcancel', handleTouchCancel, {
        capture: true,
      })
    }
  }, [isEdgeResizing])

  const isPointerCaptured = isResizing || isDragging

  // 16:9 を保つときの、窓の高さのうち動画以外の高さ。直近の窓の大きさから逆算する
  // ため、幅を変えても変わらず、Widget の一覧の開閉（通知）で変わる
  const extraHeight = getAspectLockExtraHeight(width, windowHeight)

  return (
    <>
      {/* 窓のレイヤー。画面全体を覆い、Rnd の移動範囲（bounds="parent"）を画面全体にする。
          position: fixed は必ず重ね合わせコンテキストを作るため、前面化の order は
          Rnd ではなくこのレイヤーの z-index に足す（Rnd に付けるとプレビュー窓と
          前後が入れ替わらない）。
          普段はクリックを通し、リサイズ・ドラッグ中だけ全面で受けて、ポインターが
          iframe 等に入ってもイベントを失わないようにする。 */}
      <div
        ref={layerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          userSelect: 'none',
          zIndex: `calc(var(--z-video-window) + ${order})`,
          pointerEvents: isPointerCaptured ? 'auto' : 'none',
        }}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
      >
        <Rnd
          style={{
            userSelect: 'none',
            pointerEvents: 'auto',
            visibility: visible ? 'visible' : 'collapse',
          }}
          size={{ width, height: windowHeight }}
          position={position}
          // 動画部分（窓から枠とヘッダー・一覧を除いた部分）の 16:9 を保つ
          lockAspectRatio={ASPECT_RATIO_VIDEO}
          lockAspectRatioExtraWidth={WIDGET_CHROME_WIDTH}
          lockAspectRatioExtraHeight={extraHeight}
          minWidth={MIN_WINDOW_WIDTH}
          minHeight={getMinWindowHeight(extraHeight)}
          bounds="parent"
          // 辺・角の 8 方向でリサイズできる。右側の自前のつまみも残す
          enableResizing
          resizeHandleStyles={RESIZE_HANDLE_STYLES}
          // Widget のヘッダーに重ねた帯だけでドラッグする（iframe の上では親のマウス
          // イベントが取れない）
          dragHandleClassName={DRAG_HANDLE_CLASS}
          cancel="#resize-handler"
          disableDragging={isResizing}
          onMouseDown={bringToFront}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          onResizeStart={handleEdgeResizeStart}
          onResize={handleEdgeResize}
          onResizeStop={handleEdgeResizeStop}
        >
          <div
            id="resize-handler"
            style={{
              position: 'absolute',
              top: '50%',
              right: resizeHandlerPosition,
              transform: 'translateY(-50%)',
              backgroundColor: '#17b1f1',
              padding: '10px 15px 10px 10px',
              borderRadius: '0 25px 25px 0',
              cursor: isHandleResizing ? 'grabbing' : 'grab',
              zIndex: 0, // 窓の内容より後ろに配置
              transition: 'right 0.4s',
            }}
            draggable={false}
            onMouseEnter={handleResizeHandlerMouseEnter}
            onMouseLeave={handleResizeHandlerMouseLeave}
            onMouseDown={handleStartResizing}
            onTouchStart={handleStartResizing}
          >
            <p
              style={{
                font: 'bold 1.5rem sans-serif',
                color: '#fff',
                margin: 0,
              }}
            >
              ↔︎
            </p>
          </div>

          <div
            ref={targetRef}
            className={`window-video-frame${lastReport === null ? '' : ' window-video-frame--ready'}`}
            style={{
              width,
              height: windowHeight,
              pointerEvents: isPointerCaptured ? 'none' : 'auto',
            }}
          >
            {status === 'error' && (
              <div className="window-video-error">
                <p>動画を読み込めませんでした</p>
                <button type="button" onClick={retry}>
                  再試行
                </button>
              </div>
            )}
          </div>

          {/* Widget のヘッダーの上に重ねるドラッグ帯。iframe ホスト（Widget が iframe を
              入れる要素）の外に置き、CSS でホストの上端に重ねる */}
          <div className={DRAG_HANDLE_CLASS} />
        </Rnd>
      </div>

      {/* 最小化タブはレイヤーの外に置く。レイヤーの重ね合わせコンテキストに入ると、
          z-index: calc(var(--z-video-window) + 2) が効かずプレビュー窓の下に隠れる */}
      <WindowVideoMinimized
        onClick={() => setVisible(true)}
        showVideo={visible}
      />
    </>
  )
}
